/**
 * Chatwoot user identification
 *
 * Sets Chatwoot contact identity and custom attributes when Firebase Auth and patient data
 * are available; dedupes repeated calls per uid.
 */

import type { SignedUpPatient } from '@physiofit-digital/physiofit_types';
import { callGetChatwootIdentifierHash } from './callGetChatwootIdentifierHash';
import { buildChatwootAttributesFromSignedUpPatient } from './buildChatwootAttributesFromSignedUpPatient';
import { getChatwoot } from './chatwootConfig';

/** Tracks the last uid we identified so concurrent calls do not double-fire `setUser`. */
let lastIdentifiedUid: string | null = null;

/**
 * Sets Chatwoot `setUser` and custom attributes from the signed-up patient.
 *
 * @param {Object} signedUpPatient Firestore patient snapshot.
 * @param {string} displayName Display name for the Chatwoot contact.
 * @return {Promise<void>}
 */
export async function identifyOnboardingChatwootUser(
  signedUpPatient: SignedUpPatient,
  displayName: string,
): Promise<void> {
  const hashResult = await callGetChatwootIdentifierHash();
  if (!hashResult.ok) return;

  const { identifier, identifierHash } = hashResult.data;

  if (lastIdentifiedUid === identifier) return;

  const chatwoot = getChatwoot();
  if (!chatwoot) return;

  chatwoot.setUser(identifier, {
    name: displayName,
    identifier_hash: identifierHash,
  });

  // A patient who has not installed the app yet can have neither attribute, and Chatwoot rejects
  // an empty object ("Custom attributes should have atleast one key").
  const attributes = buildChatwootAttributesFromSignedUpPatient(signedUpPatient);
  if (Object.keys(attributes).length > 0) {
    chatwoot.setCustomAttributes(attributes as Record<string, string | number | boolean>);
  }

  // Marked last so a failed run is retried on the next open instead of being deduped away.
  lastIdentifiedUid = identifier;
}

/**
 * Clears the last identified uid so the next session can call `setUser` again.
 *
 * @return {void}
 */
export function resetChatwootIdentity(): void {
  lastIdentifiedUid = null;
}
